import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '../../components/ui/PageHeader';
import { cn } from '../../lib/utils';
import { BrevoContactsPage } from './BrevoContactsPage';
import { BrevoListsPage } from './BrevoListsPage';
import { CampaignsPage } from './CampaignsPage';
import { EmailCampaignsPage } from './EmailCampaignsPage';
import { EmailSeriesPage } from './EmailSeriesPage';
import { MarketingCampaignsPage } from './MarketingCampaignsPage';
import { OffersPage } from './OffersPage';
import { SegmentsPage } from './SegmentsPage';
import { SiteListsPage } from './SiteListsPage';
import { SmartListsPage } from './SmartListsPage';

// "Dashboard" tab from the old admin's BrevoLayout isn't ported - no source
// system for it. "Marketing" (AI-drafted promo emails, marketing_campaigns)
// and "Campaigns" (n8n daily-tips drafts, email_campaigns - relabelled
// "Email Campaigns" here since "Campaigns" is already the promo-code
// offer_campaigns tab from Phase 9c) are both ported in Phase 9d.
// Tips Email + Marketing Campaigns lead — the two most-used screens — then the
// Brevo list/contact management and the promo/segment tabs. Series (the
// automated trigger emails, e.g. trial-ended winbacks) sits with the email tabs.
const TABS = [
  { key: 'email-campaigns', label: 'Tips Email' },
  { key: 'marketing-email', label: 'Marketing Campaigns' },
  { key: 'series', label: 'Series' },
  { key: 'smart-lists', label: 'Smart Lists' },
  { key: 'offers', label: 'Offers' },
  { key: 'segments', label: 'Segments' },
  { key: 'campaigns', label: 'Campaigns' },
  { key: 'contacts', label: 'Brevo Contacts' },
  { key: 'list-settings', label: 'List Settings' },
  { key: 'lists', label: 'Brevo Lists' },
] as const;
type TabKey = (typeof TABS)[number]['key'];
const DEFAULT_TAB = TABS[0].key;

export function MarketingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [pendingAiContext, setPendingAiContext] = useState<string | null>(null);
  // The promo campaign's own Brevo list, so the email tab doesn't make the
  // admin hunt for "<campaign> - TIQ Promo Codes" among every Brevo list.
  const [pendingListIds, setPendingListIds] = useState<number[]>([]);
  const activeTab: TabKey = TABS.some((t) => t.key === searchParams.get('tab'))
    ? (searchParams.get('tab') as TabKey)
    : DEFAULT_TAB;

  return (
    <div className="space-y-5">
      <PageHeader title="Marketing" description="Brevo lists and contacts, segments, offers and campaigns." />

      <div className="flex items-center gap-1 overflow-x-auto border-b border-border">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setSearchParams(tab.key === DEFAULT_TAB ? {} : { tab: tab.key })}
            className={cn(
              '-mb-px whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors',
              activeTab === tab.key ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'lists' && <BrevoListsPage />}
      {activeTab === 'contacts' && <BrevoContactsPage />}
      {activeTab === 'list-settings' && <SiteListsPage />}
      {activeTab === 'smart-lists' && <SmartListsPage />}
      {activeTab === 'offers' && <OffersPage />}
      {activeTab === 'segments' && <SegmentsPage />}
      {activeTab === 'campaigns' && (
        <CampaignsPage
          onGeneratePromoEmail={(context, brevoListId) => {
            setPendingAiContext(context);
            setPendingListIds(brevoListId ? [brevoListId] : []);
            setSearchParams({ tab: 'marketing-email' });
          }}
        />
      )}
      {activeTab === 'marketing-email' && (
        <MarketingCampaignsPage
          initialAiContext={pendingAiContext ?? undefined}
          initialListIds={pendingListIds}
          onConsumeInitialAiContext={() => {
            setPendingAiContext(null);
            setPendingListIds([]);
          }}
        />
      )}
      {activeTab === 'email-campaigns' && <EmailCampaignsPage />}
      {activeTab === 'series' && <EmailSeriesPage />}
    </div>
  );
}
